import { useState } from 'react'
import { IKycAnalysis } from '../../models/customer/ICustomer';
import { Shield } from 'lucide-react';
import { runKycAnalysisOnCustomer } from '../../api/requests';
import { showToast } from '../../utils/miscFunctions';
import Button from '../form-control/Button';
import moment from 'moment';
import { getFormattedDate } from '../../utils/miscFunctions';

type IKycAnalysisProps = {
    kycAnalysis?: IKycAnalysis;
    id: string;
    onproceed: () => void;
}

const KycAnalysis = ({ kycAnalysis, id, onproceed }: IKycAnalysisProps) => {
    const [loading, setLoading] = useState(false);
    
    const runCustomerKycAnalysis = async () => {
        try {
            if (!id) return;
            setLoading(true);
            const response: any = await runKycAnalysisOnCustomer(id);
            if (response) {
                showToast.success(response?.message ?? "Customer KYC analysis run successfully")
                onproceed()
            }
        } catch (error: any) {
            console.error(error?.description ?? 'Error running customer KYC analysis:');
        } finally {
            setLoading(false);
        }
    };

    const flagsArray = kycAnalysis?.flags?.slice(1, -1).split(', ')
    const recommendationsArray = kycAnalysis?.recommendations?.slice(1, -1).split(', ')


    return (
        <div>
            <div className="bg-white rounded-2xl shadow overflow-hidden dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 dark:bg-transparent dark:border-darkBg-border">
                    <h2 className="text-lg font-medium text-gray-900 flex items-center dark:text-darkBg-mainText">
                        <Shield className="h-5 w-5 mr-2 text-gray-500 dark:text-darkBg-mainText" />
                        KYC Analysis
                    </h2>
                </div>
                {kycAnalysis ? (
                    <div className="p-6">
                        <div className="grid grid-cols-1 gap-5">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-500">KYC Score</p>
                                    <div className="flex items-center mt-1">
                                        <div className="w-16 h-16 rounded-full flex items-center justify-center border-4 border-blue-100">
                                            <span className={`text-sm font-bold ${kycAnalysis.score >= 80 ? 'text-green-600' : kycAnalysis.score >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
                                                {kycAnalysis?.score || 0}/100
                                            </span>
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-xs text-gray-500 dark:text-darkBg-mainText">Last checked: {kycAnalysis?.updatedOn ? getFormattedDate({ date: kycAnalysis.updatedOn }) : '-'}</p>
                                            <p className="text-xs text-gray-500 mt-1 dark:text-darkBg-mainText">Method: AI</p>
                                        </div>
                                    </div>
                                </div>
                                <div className={`px-3 py-2 rounded-lg ${kycAnalysis.score >= 80 ? 'bg-green-100' : kycAnalysis.score >= 60 ? 'bg-amber-100' : 'bg-red-100'}`}>
                                    <span className={`text-sm font-medium ${kycAnalysis.score >= 80 ? 'text-green-800' : kycAnalysis.score >= 60 ? 'text-amber-800' : 'text-red-800'}`}>
                                        {kycAnalysis.score >= 80 ? 'Low Risk' : kycAnalysis.score >= 60 ? 'Medium Risk' : 'High Risk'}
                                    </span>
                                </div>
                            </div>

                            {Array.isArray(flagsArray) && flagsArray?.length > 0 && (
                                <div>
                                    <p className="text-sm font-medium text-gray-500 mb-2">Flags</p>
                                    <div className="space-y-2">
                                        {flagsArray?.map((flag, index) => (
                                            <div key={index} className={`p-3 rounded-md bg-red-50 dark:bg-darkBg`}>
                                                <div>
                                                    <p className="text-sm text-gray-600 ">{flag}</p>
                                                    <p className="text-xs text-gray-500 mt-1">Flagged on: {kycAnalysis?.updatedOn ? moment(kycAnalysis.updatedOn).format('Do MMM, YYYY') : '-'}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {recommendationsArray && recommendationsArray?.length > 0 && (
                                <div>
                                    <p className="text-sm font-medium text-gray-500 mb-2">Recommendations</p>
                                    <ul className="list-disc pl-5 space-y-1">
                                        {recommendationsArray.map((recommendation, index) => (
                                            <li key={index} className="text-sm text-gray-600 dark:text-darkBg-mainText">{recommendation}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className="pt-3 border-t border-gray-200 dark:border-darkBg-border">
                                <Button
                                    className="w-full px-4 py-2 bg-blue-50 text-blue-700 rounded-md text-sm font-medium hover:bg-blue-100 transition-colors flex items-center justify-center"
                                    onClick={runCustomerKycAnalysis}
                                    loading={loading}
                                    disabled={loading}>
                                    <Shield className="h-4 w-4 mr-2" />
                                    Run Full KYC Check
                                </Button>
                            </div>
                        </div>
                    </div>
                ) :
                    <div className="p-6">
                        <p className="text-sm text-gray-500 dark:text-darkBg-mainText">No KYC analysis available</p>

                        <div className="pt-3">
                            <Button
                                className="w-full px-4 py-2 bg-blue-50 text-blue-700 rounded-md text-sm font-medium hover:bg-blue-100 transition-colors flex items-center justify-center"
                                onClick={runCustomerKycAnalysis}
                                loading={loading}
                                disabled={loading}>
                                <Shield className="h-4 w-4 mr-2" />
                                Run Full KYC Check
                            </Button>
                        </div>
                    </div>
                }
            </div>
        </div>
    )
}

export default KycAnalysis